import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useApiSend } from '../../utils/httpClient';
import { login } from '../../endpoints/login';
import TextField from '@mui/material/TextField';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import InputAdornment from '@mui/material/InputAdornment';
import logo from '../../assets/PEO_InformalLogo_Color.png';
import { jwtDecode } from 'jwt-decode';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { Box, IconButton, Typography } from '@mui/material';
import { LoadingButton } from '@mui/lab';

import { Image } from 'mui-image';
import { resolvePostLoginDestination } from '../../utils/personas';

interface LoginResponse {
  access_token: string;
  refresh_token: string;
  session_id_cookie?: string;
  login_cookie?: string;
  requires_password_change?: boolean;
}

interface DecodedToken {
  sub: string;
  first_name: string;
  last_name: string;
  applications: string[];
  pages: string[];
  personas?: string[];
  default_route?: string;
}

const Login: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [rememberMe, setRememberMe] = useState<boolean>(false);

  const { mutate } = useApiSend(
    login,
    (data: LoginResponse) => {
      setIsLoading(false);
      try {
        if (rememberMe) {
          localStorage.setItem('email', email);
        }
        localStorage.setItem('token', data.access_token);
        localStorage.setItem('refresh', data.refresh_token);
        const decoded = jwtDecode<DecodedToken>(data.access_token);

        if (decoded.sub) {
          localStorage.setItem('user_id', decoded.sub);
        }
        if (decoded.first_name) {
          localStorage.setItem('first_name', decoded.first_name);
        }
        if (decoded.last_name) {
          localStorage.setItem('last_name', decoded.last_name);
        }
        if (decoded.applications?.length) {
          localStorage.setItem('apps', decoded.applications.join(','));
        }
        if (decoded.pages?.length) {
          localStorage.setItem('pages', decoded.pages.join(','));
        }
        if (decoded.personas) {
          localStorage.setItem('personas', decoded.personas.join(','));
        } else {
          localStorage.removeItem('personas');
        }
        if (decoded.default_route) {
          localStorage.setItem('default_route', decoded.default_route);
        } else {
          localStorage.removeItem('default_route');
        }

        // returnUrl from query (Router) or localStorage (401); else JWT default_route
        const returnUrl = resolvePostLoginDestination(
          searchParams.get('returnUrl') || localStorage.getItem('returnUrl'),
          decoded.default_route
        );

        // Check if password change is required
        if (data.requires_password_change) {
          // Store return URL for after password change (don't clear it yet)
          localStorage.setItem('returnUrl', returnUrl);
          // Verify user_id is set before navigation
          if (!localStorage.getItem('user_id')) {
            console.error('user_id not set after login, redirecting to /');
            navigate('/', { replace: true });
            return;
          }
          // Redirect to change password page
          requestAnimationFrame(() => {
            if (localStorage.getItem('user_id')) {
              navigate('/change-password', { replace: true });
            } else {
              console.error('user_id lost before navigation, redirecting to /');
              navigate('/', { replace: true });
            }
          });
          return;
        }

        // Clear returnUrl from localStorage after use (query params are ephemeral)
        localStorage.removeItem('returnUrl');

        // Verify user_id is set before navigation to prevent auth check race conditions
        if (!localStorage.getItem('user_id')) {
          console.error('user_id not set after login, redirecting to /');
          navigate('/', { replace: true });
          return;
        }

        // Use requestAnimationFrame to ensure navigation happens after React has processed
        // the localStorage update and re-rendered components. This prevents race conditions
        // where PrivateRoutes checks auth before user_id is available in the same render cycle.
        requestAnimationFrame(() => {
          // Double-check user_id is still set before navigating
          if (localStorage.getItem('user_id')) {
            navigate(returnUrl, { replace: true });
          } else {
            console.error('user_id lost before navigation, redirecting to /');
            navigate('/', { replace: true });
          }
        });
      } catch (error) {
        console.error(
          'Error decoding token or writing to localStorage:',
          error
        );
        setError('Failed to process login. Please try again.');
        setIsLoading(false);
      }
    },
    (error: Error) => {
      setError(error.message || 'An error occurred. Please try again later.');
      setIsLoading(false);
    }
  );

  const handleSubmit = async (e?: React.MouseEvent): Promise<void> => {
    setIsLoading(true);
    (mutate as any)({ email, password });
  };

  const handleRememberMe = (): void => {
    setRememberMe(!rememberMe);
    if (localStorage.getItem('email')) {
      localStorage.removeItem('email');
    }
  };
  let emailLS = localStorage.getItem('email');

  useEffect(() => {
    if (localStorage.getItem('email')) {
      setEmail(localStorage.getItem('email') || '');
      setRememberMe(true);
    }
  }, [emailLS]);

  const handleLinkPress = (e: React.MouseEvent): void => {
    e.preventDefault();
    window.open(
      'https://www.peointernational.org/about-peo/contact/',
      '_blank'
    );
  };

  const handleResetPassword = (e: React.MouseEvent): void => {
    window.location.href = '/forgot-password';
  };

  const handleKeyPress = (event: React.KeyboardEvent): void => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleSubmit();
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        width: '100%',
        px: 2,
        boxSizing: 'border-box',
      }}
    >
      <Box sx={{ width: '100%', maxWidth: 500, pt: 2, pb: 2 }}>
        <Image
          src={logo}
          alt="Logo"
          sx={{ maxWidth: '100%', width: '100%' }}
          width="100%"
        />
      </Box>
      <Box sx={{ width: '100%', maxWidth: 500 }}>
        <TextField
          variant="standard"
          required
          fullWidth
          id="email"
          label="Email Address"
          autoFocus
          value={email}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setEmail(e.target.value)
          }
        />
        <TextField
          variant="standard"
          required
          fullWidth
          autoComplete="current-password"
          label="Password"
          type={showPassword ? 'text' : 'password'}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={() => setShowPassword(!showPassword)}>
                  {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                </IconButton>
              </InputAdornment>
            ),
          }}
          id="password"
          sx={{ mt: 2 }}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setPassword(e.target.value)
          }
          onKeyDown={handleKeyPress}
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={rememberMe}
              color="primary"
              onChange={handleRememberMe}
              inputProps={{ 'aria-label': 'controlled' }}
            />
          }
          label="Remember me"
        />
        <LoadingButton
          fullWidth
          loading={isLoading}
          disabled={isLoading}
          variant="contained"
          onClick={handleSubmit}
          sx={{ mt: 1 }}
        >
          Sign In
        </LoadingButton>
        {error && (
          <Typography
            variant="overline"
            color="error"
            align="center"
            fontWeight="bold"
            display="block"
            sx={{ mt: 1 }}
          >
            {error}
          </Typography>
        )}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            pt: 1,
            gap: 2,
            flexWrap: 'wrap',
          }}
        >
          <Typography
            variant="body2"
            color="primary"
            onClick={handleResetPassword}
            sx={{ cursor: 'pointer', textDecoration: 'underline' }}
          >
            Forgot Password
          </Typography>
          <Typography
            variant="body2"
            color="primary"
            onClick={handleLinkPress}
            sx={{ cursor: 'pointer', textDecoration: 'underline' }}
          >
            Contact Us
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default Login;
